import { z } from 'zod';

const ObjectIdString = z.string().regex(/^[0-9a-fA-F]{24}$/, "Invalid ObjectId");

// 1. Schéma simple pour un mediator (Module & Activity)
const mediatorSchema = z.object({
  type: z.enum(['pdf', 'text', 'audio', 'video', 'image']),
  path: z.string().min(1),
  description: z.string().optional(),
  duration: z.number().int().min(0).optional(),
});

const preActivityReflectionSchema = z.object({
  text: z.string().min(20),
  questions: z.array(z.string().min(10)).min(1),
  duration: z.string(),
});

// 2. Interactive Resource (Module.interactiveResources)
const interactiveResourceSchema = z.object({
  type: z.string().min(1),
  url: z.string().min(1),
  description: z.string().optional(),
  simulationId: ObjectIdString.optional(),
  scenarioId: ObjectIdString.optional(),
  caseId: ObjectIdString.optional(),
});

const questionSchema = z.object({
  text: z.string().min(5),
  type: z.enum(['true-false', 'multiple-choice', 'open']),
  options: z.array(z.object({
    text: z.string(),
    isCorrect: z.boolean().optional(),
    correct: z.boolean().optional(),
  })).optional(),
  correctAnswer: z.any().optional(),
  points: z.number().int().optional(),
  feedback: z.string().optional(),
});

// 3. Quiz embarqué (diagnosticQuiz / comprehensionQuiz dans Module & Activity)
const embeddedQuizSchema = z.object({
  title: z.string().optional(),
  passingScore: z.number().int().min(0).max(100).optional(),
  questions: z.array(questionSchema).optional(), // Référence propre
}).passthrough();

const badgeSchema = z.object({
  name: z.string().min(2, "Le nom du badge est trop court"),
  description: z.string().optional(),
  level: z.enum(['bronze', 'silver', 'gold', 'platinum']).default('bronze'),
  criteria: z.string().optional(), // Ce qu'il faut faire pour l'avoir
  image: z.string().optional(),    // URL de l'icône
}).passthrough();

const recompensesSchema = z.object({
  points: z.number().min(0).default(0),
  badges: z.array(badgeSchema).default([]),
  certificationTitle: z.string().trim().optional(),
});

// 4. Central Workspace (Activity.centralWorkspace)
const centralWorkspaceSchema = z.object({
  type: z.enum([
    'none', 'table-builder', 'argument-map', 'oral-recorder', 'quote-analyzer',
    'data-dashboard', 'timeline-builder', 'custom', 'dual-builder',
    'visual-comparator', 'social-field-mapper', 'capstone-end'
  ]).default('none'),
  config: z.any().optional(),
  previewData: z.any().optional(),
}).passthrough();



// 5. RealTime (Activity.realTime – simplifié sur le model réel)
const realTimeSchema = z.object({
  enabled: z.boolean().default(false),
  wallId: ObjectIdString.optional(),
  wallTitle: z.string().optional(),
  wallDescription: z.string().optional(),
  leaderboardEnabled: z.boolean().optional(),
  streakEligible: z.boolean().optional(),
  requiredDailyAction: z.enum(['wall-post', 'upload', 'any']).optional(),
  flashChallenge: z.object({
    active: z.boolean().default(false),
    title: z.string().optional(),
    description: z.string().optional(),
    endsAt: z.string().datetime().optional(),
    bonusPoints: z.number().int().min(0).optional(),
  }).optional(),
  instantFeedback: z.object({
    enabled: z.boolean().default(false),
    maxDelayHours: z.number().int().min(0).optional(),
    mentorTags: z.array(z.string()).optional(),
  }).optional(),
  welcomeMessage: z.object({
    content: z.string().optional(),
    type: z.enum(['info', 'celebration', 'warning']).default('info'),
    isPinned: z.boolean().default(false),
  }).optional(),
}).passthrough();

const granuleStepSchema = z.object({
  stepId: z.string().min(1),
  type: z.enum([
    'experiential-trigger',
    'emotional-checklist',
    'progressive-reveal',
    'micro-quiz',
    'micro-assessment',
    'theory-invitation',
    'personal-synthesis',
    'empowering-closure',
    'modal-enrichie',
    'video-intro',
    'case-study',
    // Ajouter d'autres types au fur et à mesure
  ]),
  title: z.string().min(1).optional(),
  text: z.string().optional(),
  input: z.string().optional(), // ex: "vocal-preferred"
  placeholder: z.string().optional(),
  duration: z.number().int().min(0).optional(),
  harvestKey: z.string().optional(),
  required: z.boolean().optional(),
  validationMessage: z.string().optional(),
  options: z.array(z.string()).optional(), // pour emotional-checklist
  cards: z.array(z.object({
    id: z.string(),
    title: z.string(),
    content: z.string(),
    revealCondition: z.enum(['click', 'after-previous']).optional(),
  })).optional(), // pour progressive-reveal
  questions: z.array(questionSchema).optional(),
  finalQuestion: z.string().optional(),
  // Champs d'ancrage et de harvest
  anchorType: z.string().optional(),
  cognitiveActivation: z.string().optional(),
  pesaEchoAgent: z.string().optional(),
  pesaEchoContext: z.string().optional(),
  harvestTag: z.string().optional(),
  harvestAnalysis: z.any().optional(),
  emotionMapping: z.any().optional(),   // objet complexe
  pedagogicalNote: z.string().optional(),

  // Évaluation et feedback
  evaluationGrid: z.array(z.object({
    criterion: z.string(),
    levels: z.any(),
  })).optional(),
  harvestIntegration: z.any().optional(),
  selfAssessment: z.object({
    prompt: z.string(),
    options: z.array(z.object({
      label: z.string(),
      level: z.string(),
      emoji: z.string().optional(),
    })),
    harvestKey: z.string().optional(),
    feedbackOnGap: z.any().optional(),
  }).optional(),
  dynamicText: z.any().optional(),
  retentionMechanism: z.string().optional(),
  nextStepForeshadow: z.string().optional(),
  pesaConnectionReveal: z.string().optional(),

  // Pour emotional-checklist
  allowCustom: z.boolean().optional(),

  // Pour case-study
  situation: z.string().optional(),
  correction: z.string().optional(),
  hint: z.string().optional(),   // sur les questions
  escalatedCases: z.array(z.any()).optional(),

  // Pour micro-quiz : feedback avancé
  adaptiveFeedback: z.any().optional(),
  errorAnalysis: z.array(z.object({
    wrongAnswer: z.any(),
    cognitiveError: z.string().optional(),
    remediation: z.string().optional(),
  })).optional(),

  // Pour progressive-reveal : teaser
  teaserTitle: z.string().optional(),
  teaserText: z.string().optional(),
  teaserVisual: z.string().optional(),
  teaserDurationHint: z.string().optional(),
  modal: z.any().optional(),
  keyQuote: z.any().optional(),
  transferExample: z.string().optional(),
  pesaActionId: z.string().optional(),
  conceptualDepth: z.number().optional(),

  // Pour theory-invitation
  whyForYou: z.any().optional(),
  adaptiveLogic: z.any().optional(),
  pesaUnlockCondition: z.string().optional(),

  // Pour empowering-closure
  mood: z.string().optional(),  // déjà présent mais optionnel
  mediaReference: z.any().optional(), // theory-invitation
  resourceReference: z.any().optional(),
  actions: z.array(z.object({
    label: z.string(),
    action: z.string(),
  })).optional(),
}).passthrough();

// Schéma complet d'un granule
const granuleSchema = z.object({
  id: z.string().min(1),
  type: z.enum(['scenario', 'activity', 'capstone', 'quiz']),
  titre: z.string().min(3),
  duration: z.number().int().min(0).optional(),
  order: z.number().int().min(1),
  content: z.object({
    steps: z.array(granuleStepSchema).optional(),
    comprehensionQuiz: embeddedQuizSchema.optional(),
    videoLink: z.string().url().optional(),
    videoTitle: z.string().optional(),
    videoDescription: z.string().optional(),
    narrativeArc: z.any().optional(),
    bloomLevel: z.string().optional(),
    harveyTier: z.string().optional(),
    competencySignal: z.any().optional(),
    pesaEchoId: z.string().optional(),
    unlocksPesaContent: z.any().optional(),
    preActivityReflection: preActivityReflectionSchema.optional(),
    diagnosticQuiz: embeddedQuizSchema.optional(),
    associatedFicheId: z.string().optional(),
    mediators: z.array(mediatorSchema).optional(),
    mediatorOnFail: mediatorSchema.optional(),
    alternativePath: z.object({
      type: z.string(),
      description: z.string().optional(),
      path: z.string().optional(),
      validation: z.string().optional(),
    }).optional(),
  }).optional(),
}).passthrough();

// Schéma pour les fiches (théorie + bonus)
const ficheSchema = z.object({
  id: z.string().min(1),
  titre: z.string().min(3),
  fileType: z.string().optional(),
  filePath: z.string().optional(),
  category: z.enum(['theorie', 'bonus']),
  sourceGranuleId: z.string().optional(),
  description: z.string().optional(),
  isConceptTool: z.boolean().default(true),
  capabilities: z.array(z.string()).default(["read", "extract"]),
}).passthrough();

// Schéma pour une Activity (pour les field arrays dans les modules)
export const activitySchema = z.object({
  moduleId: z.string().min(1),
  ficheId: ObjectIdString.optional(),
  type: z.enum([
    'content-step', 'activity', 'quiz', 'micro-quiz', 'workshop', 'atelier',
    'visual-analysis', 'projet-groupe', 'peer-discussion', 'capstone',
    'live-flash', 'daily-challenge', 'Pratique / Atelier'
  ]),
  titre: z.string().min(3).optional(),
  description: z.string().min(10),
  duration: z.number().int().min(0).optional(),
  points: z.number().int().min(0).optional(),
  order: z.number().int().min(0).optional(),
  resources: z.object({
    steps: z.array(z.any()).optional(),
    validationSteps: z.array(z.any()).optional(),
    text: z.any().optional(),
    images: z.array(z.string()).optional(),
    questions: z.array(z.object({
      text: z.string(),
      type: z.enum(['open', 'multiple-choice', 'fill-in-the-blank']),
      options: z.array(z.object({
        text: z.string(),
        isCorrect: z.boolean().optional(),
      })).optional(),
      answer: z.string().optional(),
      feedback: z.string().optional(),
    })).optional(),
    fileUploads: z.array(z.object({
      label: z.string().optional(),
      required: z.boolean().optional(),
      accept: z.string().optional(),
      maxSize: z.number().optional(),
      helpText: z.string().optional(),
    })).optional(),
    liveSession: z.object({
      title: z.string().optional(),
      date: z.string().optional(),
      time: z.string().optional(),
      link: z.string().optional(),
      isFlash: z.boolean().optional(),
      announcedMinutesBefore: z.number().optional(),
      liveChat: z.boolean().optional(),
      autoNotifyNewPost: z.boolean().optional(),
      dedicatedChannel: z.string().optional(),
      isLiveNow: z.boolean().optional(),
      liveStartsAt: z.string().datetime().optional(),
      liveEndsAt: z.string().datetime().optional(),
    }).optional(),
  }).optional(),
  realTime: realTimeSchema.optional(),
  centralWorkspace: centralWorkspaceSchema.optional(),
  fichier: z.object({
    type: z.string().optional(),
    path: z.string().optional(),
    description: z.string().optional(),
  }).optional(),
  files: z.array(z.object({
    filePath: z.string(),
    fileType: z.string(),
    fileDescription: z.string().optional(),
  })).optional(),
  mediatorOnStart: z.array(mediatorSchema).optional(),
  objectifs: z.array(z.string()).optional(),
  comprehensionQuiz: embeddedQuizSchema.optional(),
  alternativePath: z.object({
    type: z.enum(['deep-video-analysis', 'short-video', 'upload-proof', 'peer-discussion', 'text-only', 'none']),
    description: z.string().optional(),
    path: z.string().optional(),
    validation: z.string().optional(),
  }).optional(),
  groupCollaboration: z.boolean().optional(),
}).passthrough();

const capstoneSchema = z.object({
  moduleId: z.string(),
  activityId: z.string().min(1, "L'identifiant d'activité est requis"),
  type: z.literal("capstone"),
  titre: z.string().min(5),
  description: z.string(),
  duration: z.number().default(2880), // 30 jours par défaut
  points: z.number().default(2000),
  order: z.number().default(999),
  groupCollaboration: z.boolean().default(true),

  // Système de narration au démarrage
  mediatorOnStart: z.array(z.object({
    type: z.enum(["audio", "video", "text"]),
    path: z.string(),
    description: z.string().optional()
  })).optional(),

  objectifs: z.array(z.string()),

  // Configuration de l'aspect "Engine" (Zone centrale)
  resources: z.object({
    steps: z.array(z.object({
      stepId: z.string(),
      type: z.string(), // ex: "experiential-trigger", "micro-quiz"
      title: z.string(),
      text: z.string().optional(),
      introduction: z.string().optional(),
      input: z.enum(["none", "vocal-preferred", "text", "file"]).optional(),
      placeholder: z.string().optional(),
      options: z.array(z.string()).optional(), // Pour emotional-checklist
      cards: z.array(z.object({ // Pour progressive-reveal
        id: z.string(),
        title: z.string(),
        content: z.string()
      })).optional(),
      questions: z.array(z.object({ // Pour micro-quiz
        text: z.string(),
        type: z.string(),
        correctAnswer: z.any(),
        feedback: z.string().optional()
      })).optional(),
      mediaReference: z.object({
        type: z.string(),
        title: z.string(),
        description: z.string(),
        duration: z.number().optional()
      }).optional(),
      actions: z.array(z.object({
        label: z.string(),
        action: z.string()
      })).optional(),
      duration: z.number().optional(),
      harvestKey: z.string().optional(), // Pour lier les réponses au profil
      required: z.boolean().default(false)
    }))
  }),

  // Configuration du Dashboard (HUD et Sidebars)
  centralWorkspace: z.object({
    type: z.literal("capstone-end"),
    config: z.object({
      title: z.string(),
      timelineEnabled: z.boolean().default(true),
      impactMap: z.boolean().default(true),
      mediaWall: z.boolean().default(true),
      petitionTracker: z.boolean().default(true),
      leaderboardNational: z.boolean().default(true),
      flashChallenges: z.boolean().default(true)
    })
  }),

  // Paramètres temps réel et Sidebars tactiques
  realTime: z.object({
    enabled: z.boolean().default(true),
    wallTitle: z.string().optional(),
    wallDescription: z.string().optional(),
    flashChallenge: z.object({
      active: z.boolean(),
      title: z.string(),
      description: z.string(),
      bonusPoints: z.number()
    }).optional(),
    instantFeedback: z.object({
      enabled: z.boolean().default(true),
      mentorTags: z.array(z.string()).optional()
    }).optional(),
    welcomeMessage: z.object({
      content: z.string(),
      isPinned: z.boolean().default(true)
    }).optional()
  }),

  // Session Live de clôture
  liveSession: z.object({
    title: z.string(),
    date: z.string(), // Format texte ex: "30 avril 2026"
    time: z.string(), // ex: "20h00"
    link: z.string().url(),
    isLiveNow: z.boolean().default(false)
  })
});

// Schéma pour créer/éditer un Module isolé (utile pour les field arrays)
export const moduleSchema = z.object({
  moduleId: z.string().min(1),
  nom: z.string().min(3),
  titre: z.string().min(3).optional(),
  description: z.string().min(10),
  objectifs: z.array(z.string()).min(1),
  duree: z.number().int().min(0).optional(),
  status: z.enum(['not started', 'started', 'in progress', 'completed', 'draft', 'published']).optional(),
  ordre: z.number().int().min(1),
  parcours: ObjectIdString.optional(),
  parcoursId: ObjectIdString.optional(),
  contenu: z.array(z.string()).optional(),
  fichier: z.object({
    type: z.string().optional(),
    path: z.string().optional(),
    description: z.string().optional(),
  }).optional(),
  preActivitiesSummary: z.string().optional(),
  preActivityReflection: preActivityReflectionSchema.optional(),
  fiches: z.array(ficheSchema).optional(),
  activities: z.array(ObjectIdString).optional(),
  quiz: z.array(ObjectIdString).optional(),
  quizId: z.array(ObjectIdString).optional(),
  diagnosticQuiz: embeddedQuizSchema.optional(),
  comprehensionQuiz: embeddedQuizSchema.optional(),
  finalQuiz: embeddedQuizSchema.optional(),
  bonusFiches: z.array(ficheSchema).optional(),
  dureeDetaillee: z.object({
    contenu: z.number().int().optional(),
    activités: z.number().int().optional(),
    quiz: z.number().int().optional(),
  }).optional(),
  notes: z.string().optional(),
  prerequis: z.array(z.string()).optional(),
  resultatsAttendus: z.array(z.string()).optional(),
  stylesApprentissage: z.array(z.enum(["Visuel", "Réflexif", "Kinesthésique", "Auditif", "Lecture/Écriture"])).optional(),
  ressourcesSupplementaires: z.array(z.string()).optional(),
  interactiveResources: z.array(interactiveResourceSchema).optional(),
  mediators: z.array(mediatorSchema).optional(),
  audioMediateur: z.string().optional(),
  granules: z.array(granuleSchema).optional(),
}).passthrough();

// 6. Schéma principal pour créer un Parcours (aligné sur Parcours model + seed)
export const parcoursSchema = z.object({
  _id: z.string().optional(),
  nom: z.string().min(3).max(100),
  description: z.string().min(10),
  slug: z.string().optional(),
  status: z.enum(["not started", "in progress", "completed", "draft", "published"]).optional(),
  visibility: z.enum(['public', 'private', 'restricted']).optional(),
  niveau: z.enum(['débutant', 'intermédiaire', 'avancé']).optional(),
  dateLancement: z.string().optional().refine(
    (val) => !val || /^\d{4}-\d{2}-\d{2}$/.test(val),
    { message: "Format de date invalide (YYYY-MM-DD)" }
  ),
  dateFin: z.string().optional().refine(
    (val) => !val || /^\d{4}-\d{2}-\d{2}$/.test(val),
    { message: "Format de date invalide (YYYY-MM-DD)" }
  ),
  dureeEstimee: z.number().int().optional(),
  tags: z.array(z.string()).optional(),
  categories: z.array(z.string()).optional(),
  competences: z.array(z.string()).optional(),
  pedagogicalObjectives: z.array(z.string()).optional(),
  recompenses: z.array(recompensesSchema).default([]), 
  engineConfig: z.object({
    isSequencial: z.boolean().default(true),
    allowSkipQuiz: z.boolean().default(false),
    autoValidateOpenQuestions: z.boolean().default(false),
    enableDataHarvesting: z.boolean().optional(),
    showContextualResources: z.boolean().default(true),

    // --- L'Intelligence de Progression FEP ---
    progressionLogic: z.object({
      weights: z.object({
        // LE BLOC PARCOURS (INTERNE)
        core: z.object({
          quizzes: z.number().default(40),
          activities: z.number().default(30),
          capstone: z.number().default(30),
        }),
        // LES BLOCS EXTERNES (DYNAMIQUES)
        external: z.object({
          streamA: z.object({ 
            label: z.string().default("Activités Scolaires"), 
            weight: z.number().default(0),
            enabled: z.boolean().default(false)
          }),
          streamB: z.object({ 
            label: z.string().default("Activités Culturelles"), 
            weight: z.number().default(0),
            enabled: z.boolean().default(false)
          }),
        })
      }),
      minimumScoreToPass: z.number().default(70),
    })
  }).optional(),
  modules: z.array(moduleSchema).min(1),
  capstone: z.array(capstoneSchema).min(1),
  metadata: z.object({
    version: z.enum(['1.0', '3.0']).optional(),
    author: z.string().optional(),
    createdAt: z.string().datetime().optional(),
    isTemplateLocked: z.boolean().optional(),
  }).optional(),
}).passthrough();
